import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Toast.css';

// Símbolos para ícones
const ICONS = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ'
};

// Gerenciador global de toasts
class ToastManager {
  constructor() {
    this.listeners = [];
    this.toasts = [];
    this.nextId = 1;
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify() {
    this.listeners.forEach(listener => listener(this.toasts));
  }

  show(options) {
    const toast = {
      id: this.nextId++,
      type: options.type || 'info',
      title: options.title,
      message: options.message,
      duration: options.duration ?? 5000,
      createdAt: Date.now()
    };

    this.toasts.push(toast);
    this.notify();

    return toast.id;
  }

  remove(id) {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.notify();
  }

  clear() {
    this.toasts = [];
    this.notify();
  }
}

const toastManager = new ToastManager();

// API pública
export const toast = {
  success: (message, title = 'Sucesso', options = {}) => 
    toastManager.show({ type: 'success', title, message, ...options }),
  
  error: (message, title = 'Erro', options = {}) => 
    toastManager.show({ type: 'error', title, message, ...options }),
  
  warning: (message, title = 'Atenção', options = {}) => 
    toastManager.show({ type: 'warning', title, message, ...options }),
  
  info: (message, title = 'Informação', options = {}) => 
    toastManager.show({ type: 'info', title, message, ...options }),
  
  remove: (id) => toastManager.remove(id),
  clear: () => toastManager.clear()
};

// Componente individual de Toast
function ToastItem({ toast: toastData, onRemove }) {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onRemove(toastData.id), 200);
  }, [toastData.id, onRemove]);

  useEffect(() => {
    if (toastData.duration > 0) {
      const timer = setTimeout(handleClose, toastData.duration);
      return () => clearTimeout(timer);
    }
  }, [toastData.duration, handleClose]);

  return (
    <div className={`toast ${toastData.type} ${isExiting ? 'exiting' : ''}`}>
      <div className="toast-icon">
        {ICONS[toastData.type]}
      </div>
      <div className="toast-content">
        {toastData.title && <div className="toast-title">{toastData.title}</div>}
        {toastData.message && <div className="toast-message">{toastData.message}</div>}
      </div>
      <button 
        className="toast-close" 
        onClick={handleClose}
        aria-label="Fechar"
      >
        ×
      </button>
      {toastData.duration > 0 && (
        <div 
          className="toast-progress"
          style={{ animationDuration: `${toastData.duration}ms` }}
        />
      )}
    </div>
  );
}

// Container de Toasts
export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    return toastManager.subscribe(setToasts);
  }, []);

  if (toasts.length === 0) return null;

  return createPortal(
    <div className="toast-container">
      {toasts.map(t => (
        <ToastItem 
          key={t.id} 
          toast={t} 
          onRemove={toastManager.remove.bind(toastManager)}
        />
      ))}
    </div>,
    document.body
  );
}

export default ToastContainer;
